import { zodResolver } from '@hookform/resolvers/zod'
import {
  type V2CreateAdminTypePropertyInput,
  V2CreateAdminTypePropertySchema
} from '@idc-forms/schemas/api-v2/admin-type-properties'
import { Controller, useForm } from 'react-hook-form'
import { useProjectsV2LookupQuery } from '@/features/projects/hooks/use-projects-v2-lookup-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

type TypePropertyV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. `project` es un `prj_`. */
  defaultValues?: Partial<V2CreateAdminTypePropertyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: V2CreateAdminTypePropertyInput) => void
  onCancel: () => void
}

/**
 * Formulario de subtipo (crear/editar) para el side panel, contra la API v2.
 * Espeja el layout del form v1 pero cambia el transporte del proyecto: se
 * alimenta del lookup v2 de proyectos (valor `prj_`). RHF + zodResolver con el
 * MISMO schema v2 que valida el backend. El `code` es único POR PROYECTO, así que
 * el proyecto es obligatorio.
 *
 * Cuando el nombre de dos proyectos coincide, la opción muestra también su código
 * (`code`) para desambiguar (convención de selects dependientes).
 */
export function TypePropertyV2Form({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: TypePropertyV2FormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<V2CreateAdminTypePropertyInput>({
    resolver: zodResolver(V2CreateAdminTypePropertySchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const { data: projects = [] } = useProjectsV2LookupQuery()
  // Un nombre duplicado entre proyectos muestra el código para desambiguar.
  const nameCounts = projects.reduce<Record<string, number>>((acc, project) => {
    acc[project.name] = (acc[project.name] ?? 0) + 1
    return acc
  }, {})

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="project"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.project)}>
                <FieldLabel htmlFor="type-property-project">Proyecto</FieldLabel>
                <NativeSelect
                  id="type-property-project"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un proyecto
                  </NativeSelectOption>
                  {projects.map((project) => (
                    <NativeSelectOption key={project.id} value={project.id}>
                      {nameCounts[project.name] > 1
                        ? `${project.name} (${project.code})`
                        : project.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldDescription>
                  El código del subtipo es único dentro del proyecto.
                </FieldDescription>
                <FieldError errors={[errors.project]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="type-property-code">Código</FieldLabel>
                <Input
                  id="type-property-code"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="type-property-name">Nombre</FieldLabel>
                <Input
                  id="type-property-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Opcional. Descripción legible del subtipo.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          {/*
            LOS ATRIBUTOS DEL PLANO, en su propio grupo.

            Van juntos y separados de la identidad del subtipo porque responden otra
            pregunta: arriba se dice CUÁL es el plano, acá CÓMO es. Y son los tres campos
            que el cliente ve en su teléfono, así que quien los carga tiene que saberlo.

            Vacío ≠ cero. Dejar el campo en blanco lo BORRA —el contrato manda `null`— y la
            app esconde el chip; escribir `0` afirma que no tiene ninguno, que es un dato
            distinto y a veces cierto (un plano sin parqueo asignado).
          */}
          <FieldDescription className="pt-1">
            Estos tres los ve el cliente en la portada de su inmueble. Se definen por PLANO: todos
            los inmuebles de este subtipo los comparten. Déjalos vacíos si aún no los tienes;
            escribir <strong>0</strong> afirma que no tiene ninguno.
          </FieldDescription>

          <Controller
            control={control}
            name="bedrooms"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.bedrooms)}>
                <FieldLabel htmlFor="type-property-bedrooms">Habitaciones</FieldLabel>
                <Input
                  id="type-property-bedrooms"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={field.value ?? ''}
                  onChange={(event) => {
                    // Vacío viaja como `null` —«borralo»— y no como `0` ni `NaN`: el
                    // contrato distingue «sin cargar» de «no tiene ninguno».
                    const raw = event.target.value
                    field.onChange(raw === '' ? null : Number(raw))
                  }}
                  autoComplete="off"
                />
                <FieldError errors={[errors.bedrooms]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="bathrooms"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.bathrooms)}>
                <FieldLabel htmlFor="type-property-bathrooms">Baños</FieldLabel>
                <Input
                  id="type-property-bathrooms"
                  type="number"
                  min={0}
                  step={0.5}
                  inputMode="decimal"
                  value={field.value ?? ''}
                  onChange={(event) => {
                    const raw = event.target.value
                    field.onChange(raw === '' ? null : Number(raw))
                  }}
                  autoComplete="off"
                />
                {/* El medio baño es una categoría real —inodoro y lavamanos, sin ducha— y
                    es lo que la app dibuja. Por eso el paso es 0.5 y no 1. */}
                <FieldDescription>Se admiten medios baños: 2.5, 3.5.</FieldDescription>
                <FieldError errors={[errors.bathrooms]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="parkingSpaces"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.parkingSpaces)}>
                <FieldLabel htmlFor="type-property-parking">Parqueos</FieldLabel>
                <Input
                  id="type-property-parking"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={field.value ?? ''}
                  onChange={(event) => {
                    const raw = event.target.value
                    field.onChange(raw === '' ? null : Number(raw))
                  }}
                  autoComplete="off"
                />
                <FieldError errors={[errors.parkingSpaces]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="type-property-active">Activo</FieldLabel>
                <Switch
                  id="type-property-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
